import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_IMAGE,
  DEFAULT_DESCRIPTION,
} from "../seo/seoData";

/**
 * Composant SEO réutilisable (react-helmet-async).
 *
 * Gère, par page : <title>, meta description / keywords, canonical,
 * balises Open Graph + Twitter, robots, et données structurées JSON-LD.
 *
 * Aucune incidence sur le design ou les fonctionnalités : ce composant
 * n'affiche rien à l'écran, il n'écrit que dans le <head>.
 *
 * Les constantes du site sont centralisées dans src/seo/seoData.js (source
 * unique partagée avec le générateur de pré-rendu statique). On les ré-exporte
 * ici pour conserver la compatibilité des imports existants (`from "./Seo"`).
 */

export { SITE_URL, SITE_NAME, DEFAULT_IMAGE, DEFAULT_DESCRIPTION };

/**
 * Nettoie un texte (HTML/retours ligne) pour en faire une meta description :
 * supprime les balises, normalise les espaces et tronque proprement (~160 car.).
 */
export const cleanDescription = (raw, max = 160) => {
  if (!raw) return DEFAULT_DESCRIPTION;
  const text = String(raw)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
};

const buildCanonical = (path) => {
  if (!path) return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  path = "/",
  image = DEFAULT_IMAGE,
  imageWidth,
  imageHeight,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  const canonical = buildCanonical(path);
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — Voyage de luxe au Maroc | Séjours, Circuits & Transport sur mesure`;

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="fr" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      {imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />

      {/* Données structurées JSON-LD */}
      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
